import React, { cloneElement, isValidElement, ReactElement, ReactNode } from 'react';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { text_styles } from '../../foundation';
import { InputStatus } from '../input/types';
import { getFormHelperStatus } from './styles';
import {
  IconCircleCheckFillBold,
  IconCircleInfoFillBold,
  IconCircleWarningFillBold,
  IconProps,
} from '../icons/generated';
import { css } from '@emotion/react';

export type FormHelperTextElement = keyof Pick<JSX.IntrinsicElements, 'div' | 'p' | 'span'>;

export interface FormHelperTextProps extends React.Attributes {
  children?: ReactNode;
  leftIcon?: ReactNode | boolean;
  /** @default 'span' */
  as?: FormHelperTextElement;
  /** @default 'info' */
  status?: InputStatus;
}

const STATUS_ICON: Record<InputStatus, ReactElement<IconProps>> = {
  info: <IconCircleInfoFillBold size={14} color='currentColor' />,
  success: <IconCircleCheckFillBold size={14} color='currentColor' />,
  error: <IconCircleWarningFillBold size={14} color='currentColor' />,
};

export const FormHelperText: React.FC<
  FormHelperTextProps & JSX.IntrinsicElements[Extract<FormHelperTextProps['as'], string>]
> = ({ as = 'span', status = 'info', leftIcon, children, className, ...props }) => {
  const _leftIcon = isValidElement(leftIcon)
    ? cloneElement(leftIcon as ReactElement<IconProps>, {
        size: 14,
        color: leftIcon.props.color ?? 'currentColor',
      })
    : leftIcon;

  return (
    <HelperTextBase
      className={classNames('pds-form-helper-text', className)}
      css={getFormHelperStatus(status)}
      as={as}
      hasIcon={!!leftIcon}
      {...props}
    >
      {leftIcon && (
        <IconWrapper className='pds-form-helper-text__icon' aria-hidden>
          {_leftIcon === true ? STATUS_ICON[status] : _leftIcon || null}
        </IconWrapper>
      )}
      {children}
    </HelperTextBase>
  );
};

interface HelperTextBaseProps extends FormHelperTextProps {
  hasIcon: boolean;
}

const HelperTextBase = styled.span<HelperTextBaseProps>`
  ${({ hasIcon }) =>
    hasIcon
      ? css`
          display: inline-flex;
          align-items: flex-start;
          gap: 4px;
        `
      : css`
          display: inline-block;
        `}
  width: auto;
  ${text_styles.Body_12_Medium};
`;
const IconWrapper = styled.span`
  flex-shrink: 0;
  height: 14px;
  margin-top: 3px;
`;
